import { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { useBranch } from "@/context/BranchContext";
import { PageHeader } from "@/components/PageHeader";
import { StatusBadge } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { Plus, Pencil, Building2 } from "lucide-react";
import { toast } from "sonner";

const empty = { name: "", code: "", roles: ["branch"], region: "", address: "", phone: "", is_active: true };

export default function Branches() {
  const { setBranches: setCtxBranches } = useBranch();
  const [branches, setBranches] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editId, setEditId] = useState(null);

  const load = () => api.get("/branches").then((r) => { setBranches(r.data); setCtxBranches(r.data); });
  useEffect(() => { load(); }, []); // eslint-disable-line

  const openNew = () => { setForm(empty); setEditId(null); setOpen(true); };
  const openEdit = (b) => { setForm({ ...empty, ...b }); setEditId(b.id); setOpen(true); };
  const save = async () => {
    if (!form.roles || form.roles.length === 0) return toast.error("Select at least one role — Retail Branch or Warehouse");
    try { if (editId) await api.put(`/branches/${editId}`, form); else await api.post("/branches", form); toast.success("Branch saved"); setOpen(false); load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const toggleRole = (role, on) => setForm((f) => {
    const s = new Set(f.roles || []);
    if (on) s.add(role); else s.delete(role);
    return { ...f, roles: Array.from(s) };
  });
  const toggleActive = async (b) => {
    try { await api.put(`/branches/${b.id}`, { ...b, is_active: b.is_active === false }); toast.success(b.is_active === false ? "Location enabled" : "Location disabled"); load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e?.target ? e.target.value : e }));

  return (
    <div>
      <PageHeader title="Branches & Locations" subtitle="Warehouse and branch network configuration"
        actions={<Button data-testid="add-branch-btn" onClick={openNew}><Plus className="h-4 w-4 mr-1.5" /> Add Location</Button>} />
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {branches.map((b) => (
          <Card key={b.id} className={`p-4 shadow-sm ${b.is_active === false ? "opacity-60" : ""}`}>
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-2"><Building2 className="h-4 w-4 text-muted-foreground" /><h3 className="font-display font-semibold text-foreground">{b.name}</h3></div>
              <Button variant="ghost" size="sm" data-testid={`edit-branch-${b.code}`} onClick={() => openEdit(b)}><Pencil className="h-3.5 w-3.5" /></Button>
            </div>
            <div className="mt-2 flex items-center gap-2 flex-wrap">
              <span className="font-mono text-xs text-muted-foreground">{b.code}</span>
              {(b.roles || [b.type]).map((r) => <StatusBadge key={r} value={r} />)}
              {b.is_active === false && <StatusBadge value="out" label="Disabled" />}
            </div>
            <p className="text-xs text-muted-foreground mt-2">{b.region} · {b.address || "—"}</p>
            <div className="mt-3 flex items-center justify-between border-t border-border pt-3">
              <span className="text-xs text-muted-foreground">{b.is_active === false ? "Location disabled" : "Active"}</span>
              <Button variant="outline" size="sm" data-testid={`toggle-branch-${b.code}`} onClick={() => toggleActive(b)}>
                {b.is_active === false ? "Enable" : "Disable"}
              </Button>
            </div>
          </Card>
        ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>{editId ? "Edit Location" : "New Location"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Name</Label><Input data-testid="branch-name-input" value={form.name} onChange={set("name")} /></div>
              <div><Label>Code</Label><Input data-testid="branch-code-input" value={form.code} onChange={set("code")} /></div>
            </div>
            <div><Label>Type</Label>
              <div className="flex gap-6 mt-2">
                <label className="flex items-center gap-2 text-sm cursor-pointer">
                  <Checkbox data-testid="branch-role-branch" checked={(form.roles || []).includes("branch")} onCheckedChange={(v) => toggleRole("branch", v)} /> Retail Branch
                </label>
                <label className="flex items-center gap-2 text-sm cursor-pointer">
                  <Checkbox data-testid="branch-role-warehouse" checked={(form.roles || []).includes("warehouse")} onCheckedChange={(v) => toggleRole("warehouse", v)} /> Warehouse
                </label>
              </div>
              <p className="text-xs text-muted-foreground mt-1.5">A location can be both — it will sell at POS and act as a stock source.</p>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Region</Label><Input value={form.region} onChange={set("region")} /></div>
              <div><Label>Phone</Label><Input value={form.phone} onChange={set("phone")} /></div>
            </div>
            <div><Label>Address</Label><Input value={form.address} onChange={set("address")} /></div>
            <div className="flex items-center gap-2 pt-1"><Switch checked={form.is_active !== false} onCheckedChange={(v) => setForm((f) => ({ ...f, is_active: v }))} data-testid="branch-active-switch" /><Label>Active (uncheck to disable without losing data)</Label></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button><Button data-testid="save-branch-btn" onClick={save}>Save</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
